import { useState } from 'react';
import { Link, useOutletContext } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { useDocumentTitle, useMe, useTid } from '../lib/hooks';
import { fmtDayMon, fmtTime, greeting, relDay } from '../lib/format';
import type { AgendaItem, AgendaResumo, HomeData, TenantInfo } from '../lib/types';
import { ErrorState, GravPill, Loading, Stat, VisitRow } from '../components/ui';
import { NcSheet } from '../components/NcSheet';
import { PickRestaurantSheet } from '../components/StartVisit';
import { AgendaCard, AgendaForm, ConfirmBlock, useAgendaActions } from '../components/AgendaParts';

export function Home() {
  const tid = useTid();
  const info = useOutletContext<TenantInfo>();
  const me = useMe().data;
  const [pick, setPick] = useState(false);
  const [ncOpen, setNcOpen] = useState<string | null>(null);
  useDocumentTitle('Início');
  const q = useQuery({ queryKey: ['home', tid], queryFn: () => api.get<HomeData>(`/api/t/${tid}/home`) });
  const equipe = info.role !== 'visualizador';
  const ag = useQuery({ queryKey: ['agenda', tid, 'resumo'], queryFn: () => api.get<AgendaResumo>(`/api/t/${tid}/agenda/resumo`), enabled: equipe });
  const actions = useAgendaActions(tid);
  const [agForm, setAgForm] = useState<AgendaItem | null>(null);
  const d = q.data;

  return (
    <div>
      <h1 className="h1" style={{ marginBottom: 2 }}>{greeting()}, {me?.user.nome.split(' ')[0]}</h1>
      <div className="sub" style={{ marginBottom: 18 }}>{d?.today ?? ' '}</div>

      {q.error ? <ErrorState message="Não foi possível carregar o início." onRetry={() => q.refetch()} /> : null}

      <div className="grid-stats" style={{ marginBottom: 16 }}>
        {d ? (
          <>
            <Stat value={d.stats.restaurantes} label="Restaurantes" />
            <Stat value={d.stats.visitas} label="Visitas registradas" />
            <Stat value={d.stats.pendencias} label="Pendências abertas" color="var(--nc)" />
          </>
        ) : (
          [0, 1, 2].map((i) => <div key={i} className="skeleton" style={{ height: 84 }} />)
        )}
      </div>

      {d?.inProgress.map((v) => (
        <Link key={v.id} to={`/t/${tid}/visita/${v.id}`} className="item" style={{ marginBottom: 10, borderColor: 'var(--accent-border)', background: '#F4F8F6', padding: 14 }}>
          <div className="dot" style={{ background: 'var(--accent)', animation: 'pulse 1.6s ease-in-out infinite' }} />
          <div className="flex1">
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>Visita em andamento · {v.restaurantNome}</div>
            <div className="small" style={{ marginTop: 2 }}>Iniciada {fmtDayMon(v.startedAt)} às {fmtTime(v.startedAt)}{v.authorNome ? ` por ${v.authorNome}` : ''} · {v.fotos} {v.fotos === 1 ? 'foto' : 'fotos'}</div>
          </div>
          <span className="none" style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--accent)' }}>Continuar →</span>
        </Link>
      ))}

      {equipe && ag.data && (
        <>
          <div style={{ marginBottom: 12 }}>
            <ConfirmBlock tid={tid} items={ag.data.confirmar} hoje={ag.data.hoje} onEdit={setAgForm} actions={actions} />
          </div>
          <section className="card" style={{ marginBottom: 14 }}>
            <div className="section-head" style={{ alignItems: 'center' }}>
              <h2 className="h2">Sua agenda de hoje</h2>
              <Link to={`/t/${tid}/agenda`} className="link-btn">Abrir agenda</Link>
            </div>
            {ag.data.doDia.length ? (
              <div className="stack gap-8">
                {ag.data.doDia.map((it) => <AgendaCard key={it.id} tid={tid} it={it} hoje={ag.data.hoje} onEdit={setAgForm} actions={actions} />)}
              </div>
            ) : (
              <div className="small" style={{ padding: '4px 0' }}>
                Nenhuma visita agendada para hoje.{' '}
                {ag.data.proximas[0] ? (
                  <>Próxima: <strong>{ag.data.proximas[0].restaurantNome}</strong> · {relDay(ag.data.proximas[0].data, ag.data.hoje).toLowerCase()}.</>
                ) : (
                  <Link to={`/t/${tid}/agenda`} className="link-btn u">Planejar a semana</Link>
                )}
              </div>
            )}
          </section>
        </>
      )}

      {info.can.writeVisits && (
        <button
          onClick={() => setPick(true)}
          className="row between gap-12"
          style={{ width: '100%', padding: 16, marginBottom: 24, background: 'var(--dark)', border: 'none', borderRadius: 13, textAlign: 'left' }}
        >
          <div>
            <div style={{ fontSize: 14.5, fontWeight: 600, color: '#fff', marginBottom: 2 }}>Iniciar visita técnica</div>
            <div style={{ fontSize: 12.5, color: '#C2CAC7' }}>Fotos, pendências e relatório no mesmo fluxo</div>
          </div>
          <div className="mono none" style={{ fontSize: 16, color: '#fff' }}>→</div>
        </button>
      )}

      <div className="grid-2">
        <section>
          <div className="section-head">
            <h2 className="h2">Últimas visitas</h2>
            <Link to={`/t/${tid}/relatorios`} className="link-btn">Ver relatórios</Link>
          </div>
          {!d ? (
            <Loading rows={3} />
          ) : d.lastVisits.length ? (
            <div className="stack gap-8">
              {d.lastVisits.map((v) => <VisitRow key={v.id} v={v} to={`/t/${tid}/relatorio/${v.id}`} />)}
            </div>
          ) : (
            <div className="empty">Nenhuma visita finalizada ainda.</div>
          )}
        </section>
        <section>
          <div className="section-head">
            <h2 className="h2">Pendências em aberto</h2>
            <span className="counter-mono">{d ? `${d.openTotal} no total` : ''}</span>
          </div>
          {!d ? (
            <Loading rows={3} />
          ) : d.openNcs.length ? (
            <div className="stack gap-8">
              {d.openNcs.map((n) => (
                <button key={n.ncId} onClick={() => setNcOpen(n.ncId)} className="item nc" style={{ alignItems: 'flex-start', gap: 11 }}>
                  <div className="dot" style={{ marginTop: 6, background: n.ageVisits > 1 ? 'var(--persist)' : 'var(--nc)' }} />
                  <div className="flex1">
                    <div style={{ fontSize: 13.5, fontWeight: 500, lineHeight: 1.35 }}>{n.titulo}</div>
                    <div className="small wrapline" style={{ marginTop: 3 }}>
                      {n.restaurantNome} · desde {fmtDayMon(n.openedAt)}{n.ageVisits > 1 ? ` · persiste há ${n.ageVisits} visitas` : ''}
                    </div>
                    {n.reply && <div className="reply">Cliente: {n.reply.texto}</div>}
                  </div>
                  <div className="none stack gap-4" style={{ alignItems: 'flex-end' }}>
                    <GravPill g={n.gravidade} />
                    <span className="small" style={{ color: 'var(--accent)' }}>fotos ›</span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="empty">Nenhuma pendência aberta.</div>
          )}
        </section>
      </div>

      <PickRestaurantSheet tid={tid} open={pick} onClose={() => setPick(false)} />
      {equipe && (
        <AgendaForm tid={tid} open={!!agForm} onClose={() => setAgForm(null)} item={agForm} dia={agForm?.data ?? ag.data?.hoje ?? ''} pessoas={[]} meId={me?.user.id ?? ''} podeTodos={false} />
      )}
      {actions.confirmNode}
      <NcSheet base={`/api/t/${tid}/ncs`} ncId={ncOpen} onClose={() => setNcOpen(null)} reportHref={(vid) => `/t/${tid}/relatorio/${vid}`} />
    </div>
  );
}
